/**
 * Shortcuts Cheat Sheet Modal
 *
 * Central overlay listing every keyboard shortcut available in the app.
 * Opened with ⌘+/ (or Ctrl+/) from anywhere in the chat experience.
 */

import React from 'react';
import { X } from 'lucide-react';
import { Button } from './button';
import { Dialog } from './dialog';
import { getKeyDisplayText, getModifierKey } from '../utils/platform';
import { cn } from '../utils/cn';

export type ShortcutsCheatSheetModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type Shortcut = {
  description: string;
  keys: string[];
};

type ShortcutGroup = {
  title: string;
  items: Shortcut[];
};

const buildGroups = (): ShortcutGroup[] => {
  const mod = getModifierKey(); // 'CMD' on mac, 'CTRL' otherwise

  return [
    {
      title: 'Navigation',
      items: [
        { description: 'New chat', keys: [mod, 'K'] },
        { description: 'Show this cheat sheet', keys: [mod, '/'] },
      ],
    },
    {
      title: 'Messaging',
      items: [
        { description: 'Send message', keys: ['ENTER'] },
        { description: 'New line in message', keys: ['SHIFT', 'ENTER'] },
      ],
    },
    {
      title: 'Chat list',
      items: [{ description: 'Rename chat inline', keys: ['Double-click title'] }],
    },
  ];
};

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd
    className={cn(
      'inline-flex items-center justify-center',
      'min-w-[24px] h-6 px-1.5',
      'rounded-md border border-border/60',
      'bg-bg-neutral-7/70 text-foreground',
      'text-[11px] font-medium font-mono',
      'shadow-[inset_0_-1px_0_rgba(0,0,0,0.08)]'
    )}
  >
    {children}
  </kbd>
);

const renderKeys = (keys: string[]): React.ReactNode => {
  return keys.map((k, i) => (
    <React.Fragment key={`${k}-${i}`}>
      {i > 0 && <span className="text-muted-foreground/50 text-xs">+</span>}
      <Kbd>{getKeyDisplayText(k)}</Kbd>
    </React.Fragment>
  ));
};

export const ShortcutsCheatSheetModal: React.FC<ShortcutsCheatSheetModalProps> = ({
  isOpen,
  onClose,
}) => {
  const groups = React.useMemo(buildGroups, []);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      aria-labelledby="shortcuts-modal-title"
      className="flex flex-col max-h-[85vh] overflow-hidden"
    >
      <div className="flex items-center justify-between p-6 border-b border-border/30">
        <div>
          <h2 id="shortcuts-modal-title" className="text-lg font-semibold text-foreground">
            Keyboard shortcuts
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Press{' '}
            <Kbd>{getKeyDisplayText(getModifierKey())}</Kbd>
            <span className="mx-1 text-muted-foreground/60">+</span>
            <Kbd>/</Kbd>{' '}
            any time to open this menu
          </p>
        </div>
        <Button onClick={onClose} variant="ghost" size="icon" aria-label="Close">
          <X className="w-5 h-5" />
        </Button>
      </div>

      <div className="p-6 space-y-6 overflow-y-auto flex-1">
        {groups.map(group => (
          <div key={group.title} className="space-y-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group.title}
            </h3>
            <div className="rounded-lg border border-border/40 divide-y divide-border/30 overflow-hidden">
              {group.items.map(item => (
                <div
                  key={item.description}
                  className="flex items-center justify-between gap-4 px-4 py-2.5 bg-background/50"
                >
                  <span className="text-sm text-foreground">{item.description}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    {item.keys.length === 1 && item.keys[0]!.includes(' ') ? (
                      // Plain-text instruction (e.g. "Double-click title")
                      <span className="text-xs text-muted-foreground italic">{item.keys[0]}</span>
                    ) : (
                      renderKeys(item.keys)
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Dialog>
  );
};

ShortcutsCheatSheetModal.displayName = 'ShortcutsCheatSheetModal';
